import { baseApi, handleQueryError } from "./baseApi";
import { makeGetRequest } from "./utils";

export type ImageExistsStatus = "yes" | "no" | "empty";

type ToolkitRegistries = {
  registry: string;
  volatileRegistry: string;
};

export const imagesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getToolkitRegistries: builder.query<ToolkitRegistries, void>({
      queryFn: async () => {
        try {
          const registries: ToolkitRegistries = await makeGetRequest({
            endpoint: "/registries",
            getSearchParams: () => ({}),
          });
          return { data: registries };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: ["ImageRegistry"],
    }),

    getToolkitImageRepositories: builder.query<string[], { volatile: boolean }>(
      {
        queryFn: async ({ volatile }) => {
          try {
            const repositories: string[] = await makeGetRequest({
              endpoint: "/registryImages",
              getSearchParams: () => ({ volatile: volatile || "" }),
            });
            return { data: repositories };
          } catch (error) {
            return handleQueryError(error);
          }
        },
        providesTags: (_result, _error, { volatile }) => [
          { type: "ImageRegistry", id: volatile ? "volatile-repos" : "repos" },
        ],
      },
    ),

    searchDockerHub: builder.query<string[], { searchString: string }>({
      queryFn: async ({ searchString }) => {
        try {
          const images: string[] = await makeGetRequest({
            endpoint: "/dockerHubSearch",
            getSearchParams: () => ({ searchString }),
          });
          return { data: images };
        } catch (error) {
          return handleQueryError(error);
        }
      },
    }),

    getToolkitImageTags: builder.query<
      string[],
      { volatile: boolean; repositoryName: string }
    >({
      queryFn: async ({ volatile, repositoryName }) => {
        try {
          const tags: string[] = await makeGetRequest({
            endpoint: "/toolkitImageTags",
            getSearchParams: () => ({
              volatile: volatile || "",
              repositoryName,
            }),
          });
          return { data: tags };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (_result, _error, { volatile, repositoryName }) => [
        {
          type: "ImageRegistry",
          id: `tags-${volatile ? "volatile" : "stable"}-${repositoryName}`,
        },
      ],
    }),

    getDockerHubLatestImageTags: builder.query<
      string[],
      { repositoryName: string }
    >({
      queryFn: async ({ repositoryName }) => {
        try {
          const tags: string[] = await makeGetRequest({
            endpoint: "/dockerHubLatestImageTags",
            getSearchParams: () => ({ repositoryName }),
          });
          return { data: tags };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (_result, _error, { repositoryName }) => [
        { type: "ImageRegistry", id: `dockerhub-tags-${repositoryName}` },
      ],
    }),

    checkToolkitImageExists: builder.query<
      ImageExistsStatus,
      { volatile: boolean; repositoryName: string }
    >({
      queryFn: async ({ volatile, repositoryName }) => {
        try {
          const exists: ImageExistsStatus = await makeGetRequest({
            endpoint: "/toolkitImageExists",
            getSearchParams: () => ({
              volatile: volatile || "",
              repositoryName,
            }),
          });
          return { data: exists };
        } catch (error) {
          return handleQueryError(error);
        }
      },
    }),

    checkDockerHubImageExists: builder.query<
      ImageExistsStatus,
      { repositoryName: string }
    >({
      queryFn: async ({ repositoryName }) => {
        try {
          const exists: ImageExistsStatus = await makeGetRequest({
            endpoint: "/dockerHubImageExists",
            getSearchParams: () => ({ repositoryName }),
          });
          return { data: exists };
        } catch (error) {
          return handleQueryError(error);
        }
      },
    }),
  }),
});

export const {
  useGetToolkitRegistriesQuery,
  useGetToolkitImageRepositoriesQuery,
  useSearchDockerHubQuery,
  useLazySearchDockerHubQuery,
  useGetToolkitImageTagsQuery,
  useGetDockerHubLatestImageTagsQuery,
  useCheckToolkitImageExistsQuery,
  useCheckDockerHubImageExistsQuery,
} = imagesApi;
